import { faCalendar, faClock } from "@fortawesome/free-regular-svg-icons";
import {
  faLocationDot,
  faMagnifyingGlass,
  faPenToSquare,
  faRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, InputAdornment, Stack, TextField, Tooltip } from "@mui/material";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { Button, Card } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { getValetTeams } from "../../../redux/Actions/maidAction";
import TipVerificationModal from "./components/TipVerificationModal";
import BulkVerificationModal from "./components/BulkVerificationModal";

const PoolTipsList = ({ canRead, service }) => {
  const [teamToVerify, setTeamToVerify] = useState(null);
  const [membersError, setMembersError] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredTeams, setFilteredTeams] = useState([]);
  const [show, setShow] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });
  const [rowSelection, setRowSelection] = useState({});

  const { userInfo } = useSelector((state) => state.userLogin);
  const { loading: payoutLoading, success: payoutSuccess } = useSelector(
    (state) => state.verifyPayment,
  );

  const { success: bulkPayoutSuccess } = useSelector(
    (state) => state.verifyBulkTeams,
  );
  const { valetTeams, totalTeams, loading, error } = useSelector(
    (state) => state.valetTeams,
  );

  const hotelId = userInfo.isAdmin ? userInfo._id : userInfo.hotel;

  const dispatch = useDispatch();

  const getTime = (timestampStr) => {
    return timestampStr ? moment(timestampStr).format("h:mm A") : "N/A";
  };

  const isTimeWithinRange = (teamTime, startTime, endTime) => {
    if (!teamTime || !startTime || !endTime) return true;

    const teamMoment = moment(teamTime, "h:mm A");
    const startMoment = moment(startTime, "HH:mm");
    const endMoment = moment(endTime, "HH:mm");

    return teamMoment.isBetween(startMoment, endMoment, null, "[]");
  };
  const openVerificationModal = (team) => {
    setShow(true);
    setTeamToVerify(team);
    setMembersError(false);
  };
  const closeVerificationModal = (team) => {
    setShow(false);
    setTeamToVerify(null);
    setMembersError(false);
  };
  const handleBulkVerify = (data) => {
    console.log("Verify bulk teams:", data);
    setShowBulkModal(true);
  };
  // Filter functions
  const handleFilter = () => {
    try {
      const start = startDate ? moment(startDate).startOf("day") : null;
      const end = endDate ? moment(endDate).endOf("day") : null;

      const filtered = valetTeams.filter((team) => {
        const teamCreatedDate = team.createdAt ? moment(team.createdAt) : null;

        const teamTime = getTime(team.createdAt);

        //  Time filter (based on createdAt)
        const isTimeInRange =
          !startTime ||
          !endTime ||
          isTimeWithinRange(teamTime, startTime, endTime);

        // Date filter (based on createdAt with full-day coverage)
        const isDateInRange =
          (!start ||
            (teamCreatedDate &&
              teamCreatedDate.isValid() &&
              teamCreatedDate.isSameOrAfter(start))) &&
          (!end ||
            (teamCreatedDate &&
              teamCreatedDate.isValid() &&
              teamCreatedDate.isSameOrBefore(end)));

        // Location filter
        const isLocationMatch =
          !searchQuery ||
          team.location?.toLowerCase().includes(searchQuery.toLowerCase());

        return isDateInRange && isLocationMatch && isTimeInRange;
      });

      setFilteredTeams(filtered);
      setFiltersApplied(true);
    } catch (err) {
      console.error("Filter error:", err);
      setFilteredTeams([]);
      setFiltersApplied(true);
    }
  };
  const clearFilters = () => {
    setStartDate("");
    setEndDate("");
    setStartTime("");
    setEndTime("");
    setSearchQuery("");
    setFilteredTeams([]);
    setFiltersApplied(false);
  };

  // Table configuration
  const data = useMemo(() => {
    const teamsToDisplay = filtersApplied ? filteredTeams : valetTeams;

    return (
      teamsToDisplay
        ?.filter((team) => !team.verified)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .map((team) => ({
          ...team,
          location:
            team.location === "$session.params.location" || !team.location
              ? "Not Defined"
              : team.location,
          createdAtFormatted: team.createdAt
            ? moment(team.createdAt).format("MM/DD/YYYY • h:mm A")
            : "N/A",
          time: getTime(team.createdAt),
          dateRange: (() => {
            const hasStart = team.startDate && moment(team.startDate).isValid();
            const hasEnd = team.endDate && moment(team.endDate).isValid();

            if (hasStart && hasEnd) {
              return `${moment(team.startDate).format("MM/DD/YYYY")} → ${moment(
                team.endDate,
              ).format("MM/DD/YYYY")}`;
            }

            if (hasStart) {
              return `From ${moment(team.startDate).format("MM/DD/YYYY")}`;
            }

            if (hasEnd) {
              return `Until ${moment(team.endDate).format("MM/DD/YYYY")}`;
            }

            return "No Date Provided";
          })(),
        })) || []
    );
  }, [filteredTeams, filtersApplied, valetTeams]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "location",
        header: "Location",
        size: 150,
      },
      {
        accessorKey: "createdAtFormatted",
        header: "Created At",
        size: 180,
        Cell: ({ cell }) => (
          <Box className="">
            <FontAwesomeIcon icon={faClock} className="me-1" />
            {cell.getValue()}
          </Box>
        ),
      },
      // {
      //   accessorKey: "time",
      //   header: "Time",
      //   size: 100,
      //   Cell: ({ cell }) => (
      //     <Box className="fst-italic">
      //       <FontAwesomeIcon icon={faClock} className="me-1" />
      //       {cell.getValue()}
      //     </Box>
      //   ),
      // },
      {
        accessorKey: "dateRange",
        header: "Tip's Date Range",
        size: 200,
      },
      {
        accessorKey: "tip",
        header: "Tip",
        size: 80,
        Cell: ({ cell }) => `$${cell.getValue()}`,
      },
      {
        accessorKey: "review",
        header: "Review",
        size: 150,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 150,
        Cell: ({ row }) => (
          <Button
            disabled={canRead}
            variant="outline-primary"
            onClick={() => {
              openVerificationModal(row.original);
            }}
          >
            <FontAwesomeIcon className="me-2" icon={faPenToSquare} />
            Verify
          </Button>
        ),
      },
    ],
    [canRead],
  );

  // Data fetching
  useEffect(() => {
    if (hotelId !== "" && hotelId !== undefined) {
      dispatch(
        getValetTeams({
          hotelId,
          service,
          page: pagination.pageIndex + 1,
          limit: pagination.pageSize,
          verified: false,
        }),
      );
    }
  }, [hotelId, pagination, payoutSuccess, bulkPayoutSuccess]);

  useEffect(() => {
    if (teamToVerify) {
      setTeamToVerify((prevTeam) => ({
        ...prevTeam,
        verified: true,
      }));
      closeVerificationModal();
    }
  }, [payoutSuccess]);

  const table = useMaterialReactTable({
    columns,
    data,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    initialState: {
      density: "compact",
    },
    state: {
      isLoading: loading,
      pagination,
      rowSelection,
    },
    manualPagination: true,
    rowCount: totalTeams || 0,
    onPaginationChange: setPagination,
    muiPaginationProps: {
      showRowsPerPage: true,
    },
    enableStickyHeader: true,
    enableTopToolbar: false,
    enableColumnFilters: false,
    enableColumnOrdering: false,
    enableSorting: false,
    enableColumnActions: false,
    muiTablePaperProps: {
      elevation: 0,
    },
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: "#f5f5f5",
        color: "#333",
      },
    },
  });

  const selectedRows = table.getSelectedRowModel().rows;

  const selectedTeams = selectedRows.map((row) => row.original);
  return (
    <>
      {/* Team Verification Modal */}
      <TipVerificationModal
        show={show}
        onHide={closeVerificationModal}
        team={teamToVerify}
        service={service}
      />

      <BulkVerificationModal
        show={showBulkModal}
        onHide={() => setShowBulkModal(false)}
        teams={selectedTeams}
        service={service}
      />

      <Card border="0" className="custom-shadow rounded-4 mb-4">
        <Card.Body>
          {/* Filters */}
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              marginBottom: 1,
              paddingTop: 1,
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "center",
              // Responsive layout
              "@media (max-width: 900px)": {
                "& > *": { width: "calc(50% - 16px)" },
              },
              "@media (max-width: 600px)": {
                "& > *": { width: "100%" },
              },
            }}
          >
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size="small"
              slotProps={{
                textField: {
                  variant: "outlined",
                },
                inputLabel: {
                  shrink: true,
                },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FontAwesomeIcon icon={faCalendar} />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="small"
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FontAwesomeIcon icon={faCalendar} />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              label="Start Time"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              size="small"
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FontAwesomeIcon icon={faClock} />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              label="End Time"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              size="small"
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FontAwesomeIcon icon={faClock} />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              label="Search Location"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FontAwesomeIcon icon={faLocationDot} />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Tooltip title="Clear Filters">
              <Button variant="outline-secondary" onClick={clearFilters}>
                <FontAwesomeIcon icon={faRotateLeft} />
              </Button>
            </Tooltip>
            <Tooltip title="Apply Filters">
              <Button onClick={handleFilter}>
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </Button>
            </Tooltip>
          </Box>

          {/* Bulk Actions */}
          {selectedTeams.length > 0 && (
            <div
              className="d-flex justify-content-between align-items-center my-2 px-3 py-2 rounded"
              style={{
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
              }}
            >
              <span className="fw-semibold text-muted">
                {selectedTeams.length} tip{selectedTeams.length > 1 ? "s" : ""}{" "}
                selected
              </span>

              <Button
                variant="outline-primary"
                className="d-flex align-items-center"
                onClick={handleBulkVerify}
              >
                <FontAwesomeIcon className="me-2" icon={faPenToSquare} />
                Verify selected
              </Button>
            </div>
          )}

          {/* Results */}
          <div className="mt-4">
            <MaterialReactTable table={table} />
          </div>
        </Card.Body>
      </Card>
    </>
  );
};

export default PoolTipsList;
